import { computed, onBeforeUnmount, onMounted, ref, unref, watch } from 'vue'
import { hasPermission, session } from '../session.js'
import { useI18n } from '../i18n.js'
import { diagnosticWindows, diagnosticSamples, diagnosticSample, startDiagnosticWindow,
  stopDiagnosticWindow, requestDiagnosticConfiguration, diagnosticErrorKey } from './ingressDiagnostics.js'

export function useIngressDiagnostics(pair, { requestTimeoutMs = 10000 } = {}) {
  const { t } = useI18n()
  const authorized = computed(() => !!session.user && hasPermission('pts2.application.use') && hasPermission('pts2.diagnostics.manage'))
  const scope = computed(() => JSON.stringify([session.user?.id, session.user?.accessVersion,
    authorized.value, unref(pair)?.stationId, unref(pair)?.controllerId,
    session.platformContext?.activeStationId, session.pts2Context?.activeControllerId]))
  const status = ref(null), samples = ref([]), detail = ref(null), nextCursor = ref(null)
  const loading = ref(true), busy = ref(false), detailLoading = ref(false)
  const draftWindowId = ref(''), appliedWindowId = ref(''), errorKey = ref('')
  const error = computed(() => errorKey.value ? t(errorKey.value) : '')
  const configurationRequest = computed(() => status.value?.configurationRequest ?? null)
  const acknowledgedConfigurationWindowId = ref(null)
  const configurationRequested = computed(() => !!status.value?.activeWindow?.id
    && (configurationRequest.value?.windowId === status.value.activeWindow.id
      || acknowledgedConfigurationWindowId.value === status.value.activeWindow.id))
  let disposed = false, epoch = 0, timer, denied = false, startRequestId = null, startRequestedAt = 0, paged = false
  let configurationRequestId = null, configurationWindowId = null
  const requests = new Map()
  const validPair = () => [unref(pair)?.stationId, unref(pair)?.controllerId].every((n) => Number.isSafeInteger(n) && n > 0)
  function clearData() {
    status.value = null; samples.value = []; detail.value = null; nextCursor.value = null
    configurationRequestId = null; configurationWindowId = null
    acknowledgedConfigurationWindowId.value = null
  }
  function reconcileConfigurationRequest() {
    const currentWindowId = status.value?.activeWindow?.id ?? null
    if (configurationWindowId !== currentWindowId) {
      configurationRequestId = null; configurationWindowId = currentWindowId
      acknowledgedConfigurationWindowId.value = null
    }
    if (currentWindowId && configurationRequest.value?.windowId === currentWindowId) acknowledgedConfigurationWindowId.value = currentWindowId
  }
  function abortAll() { for (const request of requests.values()) { request.controller.abort(); clearTimeout(request.deadline) } requests.clear() }
  function cancel(lane) { const request = requests.get(lane); if (request) { request.controller.abort(); request.finish() } }
  function begin(lane) {
    cancel(lane)
    const request = { controller: new AbortController(), epoch, timedOut: false }
    request.deadline = setTimeout(() => { request.timedOut = true; request.controller.abort() }, requestTimeoutMs)
    requests.set(lane, request)
    request.current = () => !disposed && epoch === request.epoch && requests.get(lane) === request
    request.finish = () => { clearTimeout(request.deadline); if (requests.get(lane) === request) requests.delete(lane) }
    return request
  }
  function fail(failure, request) {
    if (!request.current() || (request.controller.signal.aborted && !request.timedOut)) return
    if ([401, 403, 404, 428].includes(failure?.status)) {
      clearData(); denied = true
      for (const other of requests.values()) if (other !== request) other.controller.abort()
    }
    errorKey.value = request.timedOut ? 'pts2DiagTimeout' : diagnosticErrorKey(failure)
  }
  async function refresh({ more = false, statusOnly = false } = {}) {
    if (disposed || !authorized.value || !validPair() || busy.value) { loading.value = false; return }
    if (more && !nextCursor.value) return
    const request = begin('read'), signal = request.controller.signal
    const expectedPair = { ...unref(pair) }, windowId = appliedWindowId.value
    loading.value = true
    try {
      const currentStatus = await diagnosticWindows(expectedPair, signal)
      if (!request.current() || signal.aborted) return
      status.value = currentStatus
      reconcileConfigurationRequest()
      if (currentStatus?.activeWindow || currentStatus?.latestWindow?.state && currentStatus.latestWindow.state !== 'ACTIVE') startRequestId = null
      if (currentStatus?.availability === 'DISABLED') {
        samples.value = []; detail.value = null; nextCursor.value = null
      } else if (!statusOnly) {
        const page = await diagnosticSamples(expectedPair, { windowId, cursor: more ? nextCursor.value : '', signal })
        if (!request.current() || signal.aborted) return
        const rows = Array.isArray(page?.items) ? page.items : []
        samples.value = more ? [...samples.value, ...rows.filter((row) => !samples.value.some((old) => old.id === row.id))] : rows
        nextCursor.value = page?.nextCursor ?? null
        paged = more
      }
      denied = false; errorKey.value = ''
    } catch (failure) { fail(failure, request) }
    finally { if (request.current()) loading.value = false; request.finish() }
  }
  async function mutate(start) {
    if (disposed || busy.value || !authorized.value || !validPair()) return
    if (start && (status.value?.availability !== 'READY' || status.value?.activeWindow)) return
    const windowId = status.value?.activeWindow?.id
    if (!start && !windowId) return
    cancel('read'); loading.value = false
    const request = begin('write'), signal = request.controller.signal
    busy.value = true; errorKey.value = ''
    try {
      if (start && (!startRequestId || Date.now() - startRequestedAt >= 60000)) {
        startRequestId = crypto.randomUUID(); startRequestedAt = Date.now()
      }
      const result = start
        ? await startDiagnosticWindow(unref(pair), startRequestId, signal)
        : await stopDiagnosticWindow(unref(pair), windowId, signal)
      if (!request.current() || signal.aborted) return
      status.value = { ...status.value, activeWindow: result?.state === 'ACTIVE' ? result : null, latestWindow: result }
      if (start) status.value.configurationRequest = null
      reconcileConfigurationRequest()
      startRequestId = null
    } catch (failure) {
      fail(failure, request)
      if (failure?.status >= 400 && failure.status < 500) startRequestId = null
    } finally { if (request.current()) busy.value = false; request.finish() }
  }
  async function requestConfiguration() {
    const windowId = status.value?.activeWindow?.id
    if (disposed || busy.value || !authorized.value || !validPair() || denied
      || status.value?.availability !== 'READY' || !windowId || status.value.activeWindow.state !== 'ACTIVE'
      || configurationRequested.value) return
    reconcileConfigurationRequest()
    if (!configurationRequestId) configurationRequestId = crypto.randomUUID()
    cancel('read'); loading.value = false
    const request = begin('write'), signal = request.controller.signal
    busy.value = true; errorKey.value = ''
    try {
      const result = await requestDiagnosticConfiguration(unref(pair), windowId, configurationRequestId, signal)
      if (!request.current() || signal.aborted) return
      status.value = { ...status.value, configurationRequest: result }
      reconcileConfigurationRequest()
    } catch (failure) { fail(failure, request) }
    finally { if (request.current()) busy.value = false; request.finish() }
  }
  async function openSample(id) {
    if (disposed || !authorized.value || !validPair() || status.value?.availability === 'DISABLED') return
    const request = begin('detail'); detail.value = null; detailLoading.value = true
    try {
      const result = await diagnosticSample(unref(pair), id, request.controller.signal)
      if (request.current() && !request.controller.signal.aborted) { detail.value = result; errorKey.value = '' }
    } catch (failure) { fail(failure, request) }
    finally { if (request.current()) detailLoading.value = false; request.finish() }
  }
  function closeDetail() { cancel('detail'); detail.value = null; detailLoading.value = false }
  function applyFilter() {
    closeDetail(); samples.value = []; nextCursor.value = null
    appliedWindowId.value = draftWindowId.value
    return refresh()
  }
  watch(scope, () => {
    epoch++; abortAll(); clearData(); denied = false; startRequestId = null; paged = false
    draftWindowId.value = ''; appliedWindowId.value = ''; errorKey.value = ''; busy.value = false; detailLoading.value = false
    loading.value = authorized.value && validPair()
    const expected = epoch
    queueMicrotask(() => { if (!disposed && epoch === expected) refresh() })
  }, { immediate: true, flush: 'sync' })
  onMounted(() => { timer = setInterval(() => {
    if (!document.hidden && !loading.value && !busy.value && !denied) refresh({ statusOnly: paged })
  }, 5000) })
  onBeforeUnmount(() => { disposed = true; epoch++; abortAll(); clearInterval(timer); clearData() })
  return { authorized, status, configurationRequest, configurationRequested, samples, detail, loading, busy, detailLoading, error, nextCursor,
    draftWindowId, appliedWindowId, refresh, startCapture: () => mutate(true), stopCapture: () => mutate(false),
    requestConfiguration, applyFilter, openSample, closeDetail, more: () => refresh({ more: true }) }
}
